import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import firebase, { database, functions, messaging } from '@/plugins/firebase';
import Navigation from '@/components/Navigation.vue';
import Notifications from '@/components/Notifications.vue';
import UserMenu from "@/components/UserMenu.vue";
import Snackbar from "@/components/Snackbar.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import CardPaymentDialog from "@/components/CardPaymentDialog.vue";
import ContractSignDialog from "@/components/ContractSignDialog.vue";
import DocumentSignDialog from "@/components/DocumentSignDialog.vue";
import platform from "platform";
import moment from "moment";
import { getSeasonYear, loadSeasonYear } from "@/store/seasonYear";
const OFFLINE_OVERLAY_DELAY_MS = 5000;
let App = class App extends Vue {
    navigationDrawerOpen = true;
    listeners = [];
    offline = false;
    offlineTimer = null;
    connectivityUnsubscribe = null;
    serverSeen = false;
    deletedUserUnsubscribe = null;
    navigationVisibilityChanged(val) {
        this.navigationDrawerOpen = val;
    }
    /**
     * Sets up a Firestore listener on the current user's instructor document
     * to detect when the user is soft-deleted (deletedAt field is set).
     * When deletion is detected, forces sign out and redirects to login.
     */
    setupDeletedUserListener(userId) {
        this.deletedUserUnsubscribe = database.collection('instructors').doc(userId)
            .onSnapshot((doc) => {
            const data = doc.data();
            if (data?.deletedAt) {
                // User has been soft-deleted - force sign out
                this.handleDeletedUser();
            }
        });
    }
    /**
     * Handles the case when the current user has been soft-deleted.
     * Signs out the user and redirects to the login page.
     */
    async handleDeletedUser() {
        // Clean up the listener first to prevent multiple triggers
        this.cleanupDeletedUserListener();
        // Sign out and redirect
        await firebase.auth().signOut();
    }
    /**
     * Cleans up the deleted user listener if it exists.
     */
    cleanupDeletedUserListener() {
        if (this.deletedUserUnsubscribe) {
            this.deletedUserUnsubscribe();
            this.deletedUserUnsubscribe = null;
        }
    }
    created() {
        firebase.auth().onAuthStateChanged(async (user) => {
            if (user) {
                // Check if user is soft-deleted BEFORE setting logged-in state
                // This prevents the authenticated UI from briefly appearing for soft-deleted users
                const userDoc = await database.collection('instructors').doc(user.uid).get();
                if (userDoc.exists) {
                    const userData = userDoc.data();
                    if (userData && userData.deletedAt) {
                        // If on login page, let UserModule.auth() handle it
                        // This allows Login.vue to show the proper "account deactivated" error message
                        if (this.$route.name === 'Login') {
                            this.$store.dispatch('stopLoading');
                            return;
                        }
                        // If not on login page (e.g., page refresh, direct URL access),
                        // sign out immediately without setting logged state
                        await firebase.auth().signOut();
                        this.$store.dispatch('stopLoading');
                        return;
                    }
                }
                // Every year-based collection hangs off the season, and the
                // documents are only readable once there is a user.
                await loadSeasonYear();
                // change user state
                await this.$store.dispatch('user/login', user);
                // Set up listener for soft-deleted user detection
                this.setupDeletedUserListener(user.uid);
                this.watchFirestoreConnectivity();
                // register listeners
                this.listeners.push(this.$store.dispatch('grade/listen'));
                this.listeners.push(this.$store.dispatch('instructor/listen'));
                this.listeners.push(this.$store.dispatch('pricing/listen'));
                this.listeners.push(this.$store.dispatch('specialOperatingHours/listen'));
                this.listeners.push(this.$store.dispatch('standardOperatingHours/listen'));
                this.listeners.push(this.$store.dispatch('voucher/listen'));
                this.listeners.push(this.$store.dispatch('notification/listen', user.uid));
                this.listeners.push(this.$store.dispatch('balance/listen'));
                this.listeners.push(this.$store.dispatch('contract/listen', {
                    userId: user.uid,
                    isModerator: this.$store.getters['user/isModerator'],
                }).then((unsubscribes) => () => unsubscribes.forEach(u => u())));
                this.listeners.push(this.$store.dispatch('document/listen', {
                    userId: user.uid,
                    isModerator: this.$store.getters['user/isModerator'],
                }).then((unsubscribes) => () => unsubscribes.forEach(u => u())));
                await Promise.all(this.listeners);
                await this.$nextTick();
                // The roster comes from the backoffice API over two round trips;
                // the app must not stay blank until it arrives.
                this.$store.dispatch('kidSchool/getAll')
                    .catch((e) => console.error('Failed to load kid schools:', e));
                setTimeout(async () => {
                    let instructor = this.$store.getters['instructor/get'](user.uid);
                    if (instructor) {
                        let shouldUpdate = false;
                        // This handles the edge case when user changes their email and then resets it back.
                        // Checks instructor email against the auth email after 5 second
                        // delay to ensure that all data has been already loaded.
                        if (instructor.data.email !== user.email) {
                            instructor.data.email = user.email;
                            shouldUpdate = true;
                        }
                        // This adds current device between instructor cloud messaging devices
                        if (await this.setupFcm(instructor)) {
                            shouldUpdate = true;
                        }
                        if (shouldUpdate) {
                            await this.$store.dispatch('instructor/update', instructor);
                        }
                    }
                }, 5000);
                // NOTE: Redirect from login page is handled in Login.vue after user/auth completes.
                // This ensures soft-delete checks complete before any navigation occurs.
                // See Login.vue line 117: await this.$router.push("/")
                if (this.$store.getters['user/isAdmin']) {
                    window.getStore = async () => console.log({
                        balances: this.$store.getters['balance/all'],
                        instructors: this.$store.getters['instructor/all'],
                        transactions: await this.$store.getters['transaction/fetchAll'],
                        works: await this.$store.getters['instructorTable/fetchAll'],
                    });
                }
            }
            else {
                // Clean up deleted user listener
                this.cleanupDeletedUserListener();
                this.stopFirestoreConnectivity();
                // end listeners
                for (const endListenerPromise of this.listeners) {
                    const endListener = await endListenerPromise;
                    endListener();
                }
                // redirect from anywhere to login
                if (this.$route.name !== 'Login') {
                    await this.$router.push({ name: 'Login' });
                }
            }
            // stop the page loader
            this.$store.dispatch('stopLoading');
        });
    }
    mounted() {
        window.addEventListener('offline', this.onOffline);
        window.addEventListener('online', this.onOnline);
    }
    beforeDestroy() {
        window.removeEventListener('offline', this.onOffline);
        window.removeEventListener('online', this.onOnline);
        this.stopFirestoreConnectivity();
        this.clearOfflineTimer();
    }
    get userLoggedIn() {
        return this.$store.getters['user/isLoggedIn'];
    }
    get appLoading() {
        return this.$store.getters['isLoading'];
    }
    async getDevice(token) {
        const getClientIp = functions.httpsCallable('getClientIp');
        return {
            token: token,
            browser: `${platform.name} ${platform.version}`,
            operatingSystem: `${platform.os}`,
            ip: (await getClientIp()).data,
            datetime: moment().format('YYYY-MM-DD HH:mm:ss'),
        };
    }
    async setupFcm(instructor) {
        if (!messaging) {
            return false;
        }
        const token = await this.getMessagingToken();
        let shouldUpdate = false;
        if (token) {
            if (!instructor.data.devices) {
                const device = await this.getDevice(token);
                instructor.data.devices = [device];
                shouldUpdate = true;
            }
            else if (!instructor.data.devices.some((device) => device.token === token)) {
                const device = await this.getDevice(token);
                const oldDevice = instructor.data.devices.find((old) => {
                    return old.operatingSystem === device.operatingSystem
                        && old.browser === device.browser
                        && old.ip === device.ip;
                });
                if (oldDevice) {
                    instructor.data.devices.splice(instructor.data.devices.indexOf(oldDevice), 1, device);
                }
                else {
                    instructor.data.devices.push(device);
                }
                shouldUpdate = true;
                if (instructor.data.devices.length > 3) {
                    let oldest = null;
                    instructor.data.devices.map((current) => {
                        if (oldest === null) {
                            oldest = current;
                        }
                        if (moment(current.datetime).isBefore(oldest.datetime, 'seconds')) {
                            oldest = current;
                        }
                    });
                    if (oldest) {
                        instructor.data.devices
                            .splice(instructor.data.devices.indexOf(oldest), 1);
                    }
                }
            }
        }
        return shouldUpdate;
    }
    async getMessagingToken() {
        if (!messaging) {
            return null;
        }
        try {
            const token = await messaging.getToken();
            const permission = await Notification.requestPermission();
            return permission === 'granted' ? token : null;
        }
        catch (e) {
            console.error(e);
            return null;
        }
    }
    /**
     * Connectivity = Firestore reachability. A metadata-including listener on config/season
     * reports fromCache=true once the SDK loses the backend; browser online/offline events
     * give instant feedback on network drops. The overlay shows after a continuous offline
     * period and the page reloads on reconnect only if the overlay was shown.
     */
    watchFirestoreConnectivity() {
        this.stopFirestoreConnectivity();
        this.serverSeen = false;
        this.connectivityUnsubscribe = database.collection('config').doc('season')
            .onSnapshot({ includeMetadataChanges: true }, (snapshot) => {
            if (!snapshot.metadata.fromCache) {
                this.serverSeen = true;
                this.onOnline();
                this.checkSeason(snapshot.data()?.year);
            }
            else if (this.serverSeen) {
                this.onOffline();
            }
        }, () => this.onOffline());
    }
    /**
     * The tab booted into one season and every collection name it holds comes
     * from that year, so another season means starting over.
     */
    checkSeason(year) {
        if (year && year !== getSeasonYear()) {
            window.location.reload();
        }
    }
    stopFirestoreConnectivity() {
        if (this.connectivityUnsubscribe) {
            this.connectivityUnsubscribe();
            this.connectivityUnsubscribe = null;
        }
    }
    onOffline() {
        if (this.offlineTimer !== null || this.offline) {
            return;
        }
        this.offlineTimer = window.setTimeout(() => {
            this.offlineTimer = null;
            this.offline = true;
        }, OFFLINE_OVERLAY_DELAY_MS);
    }
    onOnline() {
        this.clearOfflineTimer();
        if (this.offline) {
            window.location.reload();
        }
    }
    clearOfflineTimer() {
        if (this.offlineTimer !== null) {
            clearTimeout(this.offlineTimer);
            this.offlineTimer = null;
        }
    }
    /**
     * Meant to save all firestore data to local storage
     * Helpful for duplicating project
     */
    async saveToStorage() {
        // TODO
    }
    /**
     * Meant to load all firestore data from local storage
     * Helpful for duplicating project
     */
    async loadFromStorage() {
        const collection = await database.collection('dashboard');
        // @ts-ignore
        await collection.doc('guides').set({ list: JSON.parse(localStorage.getItem('guides')) });
        // @ts-ignore
        await collection.doc('restrictions').set({ list: JSON.parse(localStorage.getItem('restrictions')) });
        // @ts-ignore
        await collection.doc('contacts').set({ list: JSON.parse(localStorage.getItem('contacts')) });
    }
};
App = __decorate([
    Component({
        components: { CardPaymentDialog, ConfirmDialog, ContractSignDialog, DocumentSignDialog, UserMenu, Navigation, Notifications, Snackbar },
    })
], App);
export default App;
